import { Outlet, Link, NavLink, useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, createContext, useContext } from "react";
import { api, formatApiError, logoSrc } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard, Users, ArrowLeftRight, RefreshCcw, Upload,
  Archive, Settings, ScrollText, ChevronLeft, LogOut, Copy, Trophy, Gavel, Brain,
  Landmark, Building2, Award, ChevronRight, Lock, Sparkles, BookOpen, LayoutGrid, Info,
} from "lucide-react";
import NotificationBell from "../../components/NotificationBell";
import DemoTour from "../../components/DemoTour";
import DemoFloatingCTA from "../../components/DemoFloatingCTA";
import AutoSnapshotDialog, { suggestMilestoneForPhase } from "../../components/AutoSnapshotDialog";
import PlayerTimelineDialog from "../../components/PlayerTimelineDialog";
import { PlayerTimelineProvider } from "../../lib/playerLink";
import { Link as RouterLink } from "react-router-dom";

const LeagueCtx = createContext(null);
export const useLeague = () => useContext(LeagueCtx);

export default function LeagueLayout() {
  const { leagueId } = useParams();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [myTeam, setMyTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [timelineTarget, setTimelineTarget] = useState(null); // { fanta_id, name, rooster_id }
  const openTimeline = (fanta_id, name, rooster_id) => setTimelineTarget({ fanta_id, name, rooster_id });

  const reloadWallet = async () => {
    try {
      const r = await api.get(`/leagues/${leagueId}/ai-wallet`);
      setWallet(r.data);
    } catch { /* silent — wallet is optional */ }
  };

  const reload = async () => {
    try {
      const [lgRes, tmRes] = await Promise.all([
        api.get(`/leagues/${leagueId}`),
        api.get(`/leagues/${leagueId}/teams`),
      ]);
      setLeague(lgRes.data);
      setTeams(tmRes.data);
      setMyTeam(tmRes.data.find(t => t.manager_id === user?.user_id) || null);
      // Load wallet in parallel (best-effort)
      reloadWallet();
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.user_id) reload(); /* eslint-disable-next-line */ }, [leagueId, user?.user_id]);

  // Redirect admin to /pagamento if the league is not yet active/founders
  useEffect(() => {
    if (!league || !user) return;
    const isAdminUser = league.admin_id === user.user_id || (league.co_admins || []).includes(user.user_id);
    const paid = ["active", "founders", "demo"].includes(league.plan);
    if (!paid && isAdminUser) {
      nav(`/pagamento/${leagueId}`, { replace: true });
    }
  }, [league, user, leagueId, nav]);

  if (loading || !league) {
    return <div className="min-h-screen bg-slate-950 text-slate-500 flex items-center justify-center">Caricamento…</div>;
  }

  const isAdmin = league.admin_id === user?.user_id || (league.co_admins || []).includes(user?.user_id);
  const accent = league.theme_color || "#f59e0b";

  const tabs = [
    { to: ``, label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: `rosa`, label: "Rosa", icon: Users },
    { to: `finanze`, label: "Finanze", icon: Landmark },
    { to: `stadio`, label: "Stadio", icon: Building2 },
    { to: `rinnovi`, label: "Rinnovi", icon: RefreshCcw },
    { to: `asta`, label: "Asta", icon: Gavel },
    { to: `ai-coach`, label: "AI Coach", icon: Brain },
    { to: `classifica`, label: "Classifica", icon: Trophy },
    { to: `sponsor`, label: "Sponsor", icon: Sparkles },
    { to: `storico`, label: "Albo d'Oro & Storico", icon: Archive },
    { to: `sinottica`, label: "Sinottica", icon: LayoutGrid },
    { to: `decisioni`, label: "Decisioni Admin", icon: Settings, adminOnly: true, highlight: true },
  ];

  const copyCode = () => {
    navigator.clipboard.writeText(league.code);
    toast.success(`Codice ${league.code} copiato!`);
  };

  return (
    <LeagueCtx.Provider value={{ league, teams, myTeam, isAdmin, reload, wallet, reloadWallet, phase: league.season_phase ?? 3 }}>
      <PlayerTimelineProvider value={{ open: openTimeline }}>
      <DemoTour enabled={league.plan === "demo"}/>
      <DemoFloatingCTA enabled={league.plan === "demo"}/>
      <div className="min-h-screen bg-slate-950 text-slate-100 overflow-x-hidden">
        {league.plan === "demo" && (
          <div data-testid="demo-banner" className="sticky top-0 z-[55] bg-gradient-to-r from-amber-500 via-amber-400 to-emerald-400 text-slate-950 shadow-[0_4px_16px_rgba(245,158,11,0.35)]">
            <div className="max-w-[1600px] mx-auto px-4 py-2 flex flex-wrap items-center gap-3 text-sm font-bold">
              <div className="inline-flex items-center gap-2 shrink-0">
                <span className="text-lg">🎮</span>
                <span className="uppercase text-[10px] tracking-[0.3em]">Sei nella Demo</span>
              </div>
              <div className="flex-1 min-w-0 truncate">
                I dati sono simulati · Prova tutte le funzioni · Nessun addebito, nessuna registrazione
              </div>
              <Link
                to="/register"
                data-testid="demo-signup-cta"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-950 text-white hover:bg-slate-800 text-xs uppercase tracking-widest font-black transition-colors"
              >
                Attiva la tua lega vera →
              </Link>
            </div>
          </div>
        )}
        <nav className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-xl sticky top-0 z-20">
          <div className="max-w-[1600px] mx-auto px-3 sm:px-6 h-16 flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white shrink-0" data-testid="league-back">
                <ChevronLeft size={18} /> <span className="hidden sm:inline text-sm">Dashboard</span>
              </Link>
              <div className="w-px h-8 bg-slate-800" />
              <div className="min-w-0 flex items-center gap-2">
                {logoSrc(league.logo_url) && (
                  <img src={logoSrc(league.logo_url)} alt="" className="w-8 h-8 rounded object-cover border border-slate-700"/>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {!logoSrc(league.logo_url) && <Trophy size={16} style={{ color: accent }} className="shrink-0" />}
                    <h1 className="font-black uppercase truncate text-lg leading-none" data-testid="league-name" style={{ color: accent }}>{league.name}</h1>
                    {isAdmin && <span className="text-[9px] tracking-widest uppercase font-bold px-1.5 py-0.5 rounded border shrink-0" style={{ color: accent, backgroundColor: `${accent}22`, borderColor: `${accent}55` }}>👑 Admin</span>}
                  </div>
                  <div className="text-[10px] tracking-widest text-slate-500 uppercase mt-0.5">Stagione {league.season} · <button onClick={copyCode} className="hover:text-amber-400 inline-flex items-center gap-1" data-testid="copy-code">Codice {league.code} <Copy size={9}/></button></div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {myTeam && (
                <div className="hidden md:block text-right">
                  <div className="text-[10px] tracking-widest text-slate-500 uppercase">💰 Budget</div>
                  <div className="font-mono font-bold text-emerald-400" data-testid="my-team-budget">{Math.round(Number(myTeam.budget || 0))}</div>
                </div>
              )}
              <AIWalletBadge wallet={wallet} leagueId={leagueId}/>
              <NotificationBell leagueId={league.league_id}/>
              <RouterLink
                to={`/lega/${league.league_id}/guida`}
                title="Guida della lega"
                data-testid="league-guida-icon"
                className="p-1.5 rounded-md text-slate-400 hover:text-amber-300 hover:bg-amber-500/10 transition-colors"
              >
                <BookOpen size={16}/>
              </RouterLink>
              <button onClick={() => { logout(); nav("/"); }} className="text-sm text-slate-400 hover:text-rose-400 inline-flex items-center gap-1" data-testid="league-logout">
                <LogOut size={14} /> <span className="hidden sm:inline">Esci</span>
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 pt-4 pb-2">
          <PhaseBanner league={league} isAdmin={isAdmin} reload={reload}/>
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto scrollbar-none mt-4 pb-1">
            {tabs.filter(t => !t.adminOnly || isAdmin).map(t => (
              <NavLink key={t.to} to={t.to} end={t.end}
                className={({isActive}) =>
                  `group relative inline-flex flex-col items-center justify-center gap-1 px-3 sm:px-4 py-2.5 rounded-2xl border transition-all shrink-0 min-w-[76px] sm:min-w-[92px] ${
                    isActive
                      ? "border-transparent shadow-lg -translate-y-0.5"
                      : t.highlight
                        ? "border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 hover:border-amber-500/50"
                        : "border-slate-800 bg-slate-900/50 hover:bg-slate-800/60 hover:border-slate-600 hover:-translate-y-0.5"
                  }`}
                style={({isActive}) => isActive ? {
                  background: `linear-gradient(160deg, ${accent} 0%, ${accent}dd 100%)`,
                  boxShadow: `0 8px 20px -8px ${accent}66, inset 0 1px 0 rgba(255,255,255,0.15)`,
                  color: "#0b1220",
                } : undefined}
                data-testid={`nav-${t.to || "dashboard"}`}>
                {({isActive}) => (
                  <>
                    <t.icon size={16} className={isActive ? "" : t.highlight ? "text-amber-300" : "text-slate-400 group-hover:text-white transition-colors"}/>
                    <span className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.12em] whitespace-nowrap leading-none ${
                      isActive ? "text-slate-900" : t.highlight ? "text-amber-200" : "text-slate-400 group-hover:text-white transition-colors"
                    }`}>{t.label}</span>
                    {t.highlight && !isActive && (
                      <span className="absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"/>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </div>

        <main className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
          <Outlet />
        </main>
      </div>
      <PlayerTimelineDialog
        open={!!timelineTarget}
        fantaId={timelineTarget?.fanta_id}
        roosterId={timelineTarget?.rooster_id}
        playerName={timelineTarget?.name}
        onClose={() => setTimelineTarget(null)}
      />
      </PlayerTimelineProvider>
    </LeagueCtx.Provider>
  );
}

export { LeagueCtx };

/* -------------------- PhaseBanner: workflow guidato -------------------- */
const PHASE_META = [
  { id: 0,  emoji: "🌱", label: "Creazione stagione",       desc: "Nuova stagione appena creata, dati economici azzerati." },
  { id: 1,  emoji: "📥", label: "Import Rose",              desc: "Importazione rose iniziali da file esterno.", hint_excel: true },
  { id: 2,  emoji: "🛍️", label: "Calcolo Merch Base",       desc: "Assegnazione automatica del merchandising base per giocatore." },
  { id: 3,  emoji: "🔄", label: "Mercato di Riparazione",    desc: "Mercato aperto durante la stagione: import diff, trasferimenti manuali, operazioni.", hint_excel: true },
  { id: 4,  emoji: "🏁", label: "Fine Campionato",           desc: "Mercato bloccato. Si abilitano le funzioni economiche di chiusura." },
  { id: 5,  emoji: "📋", label: "Inserimento Risultati",     desc: "L'admin inserisce i piazzamenti finali delle competizioni." },
  { id: 6,  emoji: "💰", label: "Calcolo Economico",         desc: "Premi, merch rivalutato, bonus, stadio: patrimonio provvisorio." },
  { id: 7,  emoji: "⭐", label: "Aggiornamento Ranking",     desc: "Ranking stagionale + storico aggiornati." },
  { id: 8,  emoji: "🔓", label: "Apertura Rinnovi",          desc: "Manager decidono rinnovi/svincoli per ogni giocatore." },
  { id: 9,  emoji: "✅", label: "Approvazione Rinnovi",      desc: "Admin approva. Monte stipendi e budget definitivi." },
  { id: 10, emoji: "🎯", label: "Asta Furti",                desc: "Sessione asta post-rinnovi. Riacquisto giocatori svincolati." },
  { id: 11, emoji: "🧾", label: "Chiusura Economica",        desc: "Ricalcolo finale: budget ufficiale per la nuova stagione." },
  { id: 12, emoji: "📦", label: "Archiviazione",             desc: "Rose, budget, trofei archiviati nello storico." },
  { id: 13, emoji: "🏆", label: "Albo d'Oro",                desc: "Aggiornamento automatico di campionato, coppe, ranking, record." },
  { id: 14, emoji: "🌟", label: "Nuova Stagione",            desc: "Creata la nuova stagione. Il ciclo ricomincia da fase 1." },
];

function PhaseBanner({ league, isAdmin, reload }) {
  const [advancing, setAdvancing] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const current = league.season_phase ?? 3;
  const cur = PHASE_META.find(p => p.id === current) || PHASE_META[0];
  const next = current < 14 ? PHASE_META[current + 1] : PHASE_META[1]; // dopo 14 → 1 (ciclo)
  const prev = current > 0 ? PHASE_META[current - 1] : null;

  const doAdvance = async () => {
    setAdvancing(true);
    try {
      const r = await api.post(`/leagues/${league.league_id}/phase/advance`);
      const info = r.data.current_info;
      toast.success(`${info.emoji} Nuova fase: ${info.label}`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAdvancing(false); setSnapshotOpen(false); }
  };

  const advance = () => {
    // Iter 68: se c'è una milestone suggerita per la fase corrente → apri dialog
    if (suggestMilestoneForPhase(current)) {
      setSnapshotOpen(true);
    } else {
      if (!window.confirm(`Avanzare alla fase ${next.id}: ${next.emoji} ${next.label}?`)) return;
      doAdvance();
    }
  };

  const rollback = async () => {
    if (!prev) return;
    if (!window.confirm(`⚠️ Tornare indietro alla fase ${prev.id}: ${prev.emoji} ${prev.label}?\n\nUsa solo in caso di errore nell'avanzamento.`)) return;
    setRollingBack(true);
    try {
      const r = await api.post(`/leagues/${league.league_id}/phase/rollback`);
      const info = r.data.current_info;
      toast.success(`↩️ Tornato a: ${info.emoji} ${info.label}`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRollingBack(false); }
  };

  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const r = await api.get(`/leagues/${league.league_id}/phase/history`);
      setHistory(r.data.history || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setHistoryLoading(false); }
  };

  // ---- Vista compatta per manager (Iter 71.1): pill + info modal read-only ---
  if (!isAdmin) {
    return (
      <>
        <button
          type="button"
          onClick={() => setShowInfo(true)}
          data-testid="phase-banner-compact"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-indigo-500/30 bg-indigo-500/5 hover:bg-indigo-500/10 text-slate-200 transition-colors"
          title="Vedi tutte le fasi della stagione"
        >
          <span className="text-lg leading-none">{cur.emoji}</span>
          <div className="text-left">
            <div className="text-[9px] uppercase tracking-widest text-indigo-300 font-black">
              Fase {cur.id} / 14
            </div>
            <div className="text-xs font-bold leading-tight">{cur.label}</div>
          </div>
          <Info size={12} className="text-indigo-400"/>
        </button>

        {showInfo && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowInfo(false)} data-testid="phase-info-modal">
            <div onClick={e => e.stopPropagation()}
              className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl bg-slate-900 border border-indigo-500/40 shadow-2xl">
              <div className="p-4 border-b border-indigo-500/30 flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-black">Workflow Stagione</div>
                  <div className="text-lg font-black text-white">Fasi della lega · sola lettura</div>
                </div>
                <button onClick={() => setShowInfo(false)} className="text-slate-500 hover:text-white p-1"
                  data-testid="phase-info-close">
                  <ChevronLeft size={18}/>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <div className="text-xs text-slate-400 mb-3">
                  L&apos;admin controlla l&apos;avanzamento. Sei attualmente in <b className="text-amber-300">Fase {cur.id}: {cur.emoji} {cur.label}</b>.
                </div>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {PHASE_META.map(p => (
                    <div key={p.id}
                      className={`flex items-start gap-2 p-2 rounded-lg border text-xs ${
                        p.id === current ? "bg-amber-500/15 border-amber-500/50 text-amber-100"
                        : p.id < current ? "bg-emerald-500/5 border-emerald-500/20 text-slate-400"
                        : "bg-slate-950/40 border-slate-800 text-slate-500"
                      }`}
                      data-testid={`phase-info-row-${p.id}`}
                    >
                      <span className="text-lg leading-none shrink-0">{p.emoji}</span>
                      <div className="min-w-0">
                        <div className="font-bold flex items-center gap-1.5">
                          <span className="font-mono text-[9px] opacity-60">#{String(p.id).padStart(2,'0')}</span>
                          <span>{p.label}</span>
                          {p.id === current && <span className="ml-1 px-1 rounded bg-amber-400 text-amber-950 text-[8px] font-mono uppercase">ATTUALE</span>}
                        </div>
                        <div className="text-[10px] opacity-80 leading-snug mt-0.5">{p.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="border border-indigo-500/30 rounded-2xl bg-gradient-to-r from-indigo-950/50 via-slate-900 to-slate-950 shadow-xl p-3" data-testid="phase-banner">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setShowInfo(v => !v)}
          data-testid="phase-info-toggle"
          className="flex items-center gap-2 shrink-0 group hover:bg-indigo-500/10 rounded-lg p-1 -m-1 transition-colors"
          title="Mostra spiegazione fasi"
        >
          <span className="text-2xl">{cur.emoji}</span>
          <div className="text-left">
            <div className="text-[9px] uppercase tracking-[0.3em] text-indigo-300 font-black flex items-center gap-1">
              Fase {cur.id} / 14 <span className="text-indigo-400 group-hover:text-indigo-200">ⓘ</span>
            </div>
            <div className="font-black text-white text-sm leading-tight">{cur.label}</div>
          </div>
        </button>
        {/* Progress bar */}
        <div className="flex-1 min-w-[200px]">
          <div className="flex gap-0.5">
            {PHASE_META.filter(p => p.id > 0).map(p => (
              <div key={p.id} title={`${p.emoji} ${p.label}`}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  p.id < current ? "bg-emerald-500/70"
                  : p.id === current ? "bg-amber-400"
                  : "bg-slate-800"
                }`}
                data-testid={`phase-dot-${p.id}`}
              />
            ))}
          </div>
        </div>
        {isAdmin ? (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={openHistory}
              data-testid="phase-history-btn"
              title="Storia avanzamenti fasi"
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border border-slate-700 bg-slate-800/60 hover:bg-slate-800 text-slate-300 text-xs font-mono uppercase tracking-widest"
            >
              📜 Storia
            </button>
            {prev && (
              <button
                onClick={rollback}
                disabled={rollingBack || advancing}
                data-testid="rollback-phase-btn"
                title={`Torna a ${prev.emoji} ${prev.label}`}
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 text-xs font-mono uppercase tracking-widest"
              >
                {rollingBack ? "…" : <>↩ Indietro</>}
              </button>
            )}
            <button
              onClick={advance}
              disabled={advancing || rollingBack}
              data-testid="advance-phase-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-indigo-500/30"
            >
              {advancing ? "..." : (
                <>
                  <span>{next.emoji}</span> {current === 14 ? "Nuova Stagione" : `Avanza → ${next.label}`}
                  <ChevronRight size={12}/>
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="text-[10px] text-slate-500 flex items-center gap-1 shrink-0" data-testid="phase-locked-info">
            <Lock size={10}/> L&apos;admin controlla il workflow
          </div>
        )}
      </div>

      {/* Current phase description + Excel hint */}
      <div className="mt-2 text-xs text-slate-400 flex items-start gap-2">
        <span className="text-indigo-400 font-mono uppercase tracking-widest text-[10px] shrink-0 mt-0.5">Cosa fare</span>
        <span className="flex-1">{cur.desc}</span>
        {cur.hint_excel && isAdmin && (
          <Link to={`/lega/${league.league_id}/rosa`} data-testid="phase-excel-hint"
            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 font-mono uppercase text-[10px] tracking-widest hover:bg-emerald-500/25">
            📊 Importa Excel
          </Link>
        )}
      </div>

      {/* Full phase list panel (collapsible) */}
      {showInfo && (
        <div className="mt-3 pt-3 border-t border-indigo-500/20" data-testid="phase-info-panel">
          <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-300 mb-2">Workflow completo · 15 fasi</div>
          <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
            {PHASE_META.map(p => (
              <div key={p.id}
                className={`flex items-start gap-2 p-2 rounded-lg border text-xs ${
                  p.id === current ? "bg-amber-500/15 border-amber-500/50 text-amber-100"
                  : p.id < current ? "bg-emerald-500/5 border-emerald-500/20 text-slate-400"
                  : "bg-slate-950/40 border-slate-800 text-slate-500"
                }`}
                data-testid={`phase-info-row-${p.id}`}
              >
                <span className="text-lg leading-none shrink-0">{p.emoji}</span>
                <div className="min-w-0">
                  <div className="font-bold flex items-center gap-1.5">
                    <span className="font-mono text-[9px] opacity-60">#{String(p.id).padStart(2,'0')}</span>
                    <span>{p.label}</span>
                    {p.id === current && <span className="ml-1 px-1 rounded bg-amber-400 text-amber-950 text-[8px] font-mono uppercase">ATTUALE</span>}
                  </div>
                  <div className="text-[10px] opacity-80 leading-snug mt-0.5">{p.desc}</div>
                  {p.hint_excel && <div className="text-[9px] text-emerald-400 mt-0.5">📊 Consigliato: import Excel</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History modal */}
      {showHistory && (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowHistory(false)}>
          <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 border border-indigo-500/40 shadow-2xl"
            onClick={e => e.stopPropagation()} data-testid="phase-history-modal">
            <div className="sticky top-0 flex items-center justify-between p-4 border-b border-indigo-500/30 bg-slate-950/70 backdrop-blur">
              <div className="flex items-center gap-2">
                <span className="text-xl">📜</span>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-300">Registro modifiche fase</div>
                  <div className="text-lg font-bold text-slate-100">Storia della lega</div>
                </div>
              </div>
              <button onClick={() => setShowHistory(false)} data-testid="phase-history-close"
                className="p-2 rounded hover:bg-slate-800 text-slate-400"><ChevronLeft size={16}/></button>
            </div>
            <div className="p-4">
              {historyLoading ? (
                <div className="text-center py-8 text-slate-500 uppercase tracking-widest text-xs">Caricamento…</div>
              ) : history.length === 0 ? (
                <div className="text-center py-8 text-slate-600 italic">Nessuna modifica di fase registrata</div>
              ) : (
                <div className="space-y-2">
                  {history.map((h, i) => {
                    const meta = h.meta || {};
                    const ACTIONS = {
                      phase_advanced: { icon: "⬆️", label: "Avanzato", color: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5" },
                      phase_rolled_back: { icon: "↩️", label: "Rollback", color: "text-rose-400 border-rose-500/30 bg-rose-500/5" },
                      phase_set: { icon: "🔧", label: "Set manuale", color: "text-amber-400 border-amber-500/30 bg-amber-500/5" },
                      phase_new_season: { icon: "🌟", label: "Nuova stagione", color: "text-purple-400 border-purple-500/30 bg-purple-500/5" },
                      stadium_income_applied: { icon: "🏟️", label: "Bonus stadio applicato", color: "text-cyan-400 border-cyan-500/30 bg-cyan-500/5" },
                    };
                    const act = ACTIONS[h.action] || { icon: "•", label: h.action, color: "text-slate-400 border-slate-700 bg-slate-900" };
                    return (
                      <div key={i} className={`p-3 rounded-lg border ${act.color}`} data-testid={`history-row-${i}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 text-sm font-bold">
                            <span className="text-lg">{act.icon}</span>
                            <span>{act.label}</span>
                            {meta.from !== undefined && meta.to !== undefined && (
                              <span className="font-mono text-xs text-slate-500">Fase {meta.from} → {meta.to}</span>
                            )}
                            {meta.label && (
                              <span className="text-slate-300 text-xs">· {meta.label}</span>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-500 font-mono">
                            {h.created_at ? new Date(h.created_at).toLocaleString("it-IT") : "—"}
                          </div>
                        </div>
                        <div className="text-xs text-slate-400">
                          <span className="text-slate-500">Autore:</span> <span className="font-bold text-slate-300">{h.user_name || "?"}</span>
                          {h.user_email && <span className="text-slate-600 ml-1 text-[10px]">({h.user_email})</span>}
                          {h.action === "stadium_income_applied" && meta.total_income !== undefined && (
                            <span className="ml-3 text-cyan-300">Totale erogato: <span className="font-mono font-bold">+{meta.total_income} cr</span> a {meta.teams_count} team</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Iter 68: dialog auto-snapshot pre-avanzamento */}
      <AutoSnapshotDialog
        open={snapshotOpen}
        leagueId={league.league_id}
        season={league.season}
        currentPhase={current}
        nextPhaseLabel={`${next.emoji} ${next.label}`}
        onClose={() => setSnapshotOpen(false)}
        onConfirm={() => doAdvance()}
      />
    </div>
  );
}

/* -------------------- AI Wallet Badge (mini widget in header) -------------------- */
function AIWalletBadge({ wallet, leagueId }) {
  if (!wallet) return null;
  const { free_left, paid_left, unlimited } = wallet;
  const total = free_left + paid_left;
  const empty = !unlimited && total === 0;
  const bgTone = unlimited
    ? "border-violet-500/40 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20"
    : empty
      ? "border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20"
      : "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20";
  return (
    <RouterLink
      to={`/lega/${leagueId}/ai-coach`}
      data-testid="ai-wallet-badge"
      title={
        unlimited
          ? "AI Coach illimitato per admin/co-admin"
          : `${free_left} chiamate gratis + ${paid_left} pagate rimanenti`
      }
      className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-bold transition-colors ${bgTone}`}
    >
      <Sparkles size={12} />
      <span className="hidden lg:inline">AI</span>
      {unlimited ? (
        <span className="font-mono">∞</span>
      ) : (
        <span className="font-mono">
          {total}
          {free_left > 0 && (
            <span className="text-emerald-400/80 ml-0.5">·{free_left}f</span>
          )}
        </span>
      )}
    </RouterLink>
  );
}

